/* ==========================================================================
   Commerce Ripple — ring diagram interaction (area-based hit testing).
   A pointer maps to the ring whose AREA it falls in (smallest enclosing
   circle), so hovering ANY part of a ring activates it — no dead gaps.
   Outside every circle → intro.
   Desktop (>=1240px): hover activates; leaving the diagram → intro.
   Mobile (<1240px): tap activates (tap outside the circles → intro).
   Labels stay real buttons for keyboard/AT. Without JS the intro stays visible.
   Auto play (schema-driven, data-autoplay): cycles through the rings on a
   timer. It only runs while the section is on screen, stops for
   prefers-reduced-motion, pauses while the visitor interacts (hover on
   desktop, tap/focus anywhere) and resumes shortly after they stop.
   ========================================================================== */
(function () {
  'use strict';

  var desktop = window.matchMedia('(min-width: 1240px)');
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var inited = new WeakSet();

  function setup(section) {
    if (!section || inited.has(section)) return;
    inited.add(section);

    var diagram = section.querySelector('.ripple__diagram');
    var svg = section.querySelector('.ripple__rings');
    var rings = section.querySelectorAll('.ripple__ring');
    var labels = section.querySelectorAll('.ripple__label');
    var leftPanels = section.querySelectorAll('.ripple__text--left .ripple__panel');
    var rightPanels = section.querySelectorAll('.ripple__text--right .ripple__panel');
    var live = section.querySelector('.ripple__live');
    if (!diagram || !svg || !rings.length) return;

    // Read each ring's circle geometry from the SVG.
    var geom = [];
    for (var g = 0; g < rings.length; g++) {
      var c = rings[g].querySelector('circle');
      if (!c) continue;
      geom.push({
        cx: parseFloat(c.getAttribute('cx')),
        cy: parseFloat(c.getAttribute('cy')),
        r: parseFloat(c.getAttribute('r')),
      });
    }
    var vb =
      svg.viewBox && svg.viewBox.baseVal && svg.viewBox.baseVal.width
        ? svg.viewBox.baseVal
        : { width: 675, height: 680 };

    var current = -1; // -1 = intro

    function setPanels(list, index) {
      for (var i = 0; i < list.length; i++) {
        var p = list[i];
        var match =
          (index === -1 && p.dataset.panel === 'intro') ||
          p.dataset.panel === String(index);
        p.classList.toggle('is-active', match);
      }
    }

    function activate(index) {
      if (index === current) return;
      current = index;
      for (var i = 0; i < rings.length; i++) {
        rings[i].classList.toggle('is-active', i === index);
      }
      for (var j = 0; j < labels.length; j++) {
        var on = j === index;
        labels[j].classList.toggle('is-active', on);
        labels[j].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      setPanels(leftPanels, index);
      setPanels(rightPanels, index);
      if (live && index >= 0 && labels[index]) {
        live.textContent = labels[index].textContent.trim();
      }
    }

    function deactivate() {
      activate(-1);
    }

    /* ---- Auto play -------------------------------------------------- */
    var autoplayOn = section.hasAttribute('data-autoplay') && !reducedMotion.matches;
    var autoplayInterval = parseInt(section.getAttribute('data-autoplay-interval'), 10) || 4000;
    var includeIntro = section.getAttribute('data-autoplay-include-intro') === 'true';
    var reverse = section.getAttribute('data-autoplay-direction') === 'reverse';
    var autoplayTimer = null;
    var resumeTimer = null;
    var visible = false;

    function autoplayStep() {
      // Forward: first → last ring; reverse: last → first. After the final
      // step: back to the intro (if included) or wrap to the starting ring.
      var next;
      if (reverse) {
        next = current === -1 ? rings.length - 1 : current - 1;
        if (next < 0) next = includeIntro ? -1 : rings.length - 1;
      } else {
        next = current + 1;
        if (next >= rings.length) next = includeIntro ? -1 : 0;
      }
      activate(next);
    }

    function autoplayStart() {
      if (!autoplayOn || !visible || autoplayTimer) return;
      autoplayTimer = setInterval(autoplayStep, autoplayInterval);
    }

    function autoplayStop() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
      if (resumeTimer) {
        clearTimeout(resumeTimer);
        resumeTimer = null;
      }
    }

    // Pause for user input; resume once they've been idle for one interval.
    function autoplayPause() {
      if (!autoplayOn) return;
      autoplayStop();
      resumeTimer = setTimeout(autoplayStart, autoplayInterval);
    }

    if (autoplayOn) {
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            visible = entries[0].isIntersecting;
            if (visible) autoplayStart();
            else autoplayStop();
          },
          { threshold: 0.3 }
        ).observe(section);
      } else {
        visible = true;
        autoplayStart();
      }

      document.addEventListener('shopify:section:unload', function (e) {
        if (e.target && e.target.contains(section)) autoplayStop();
      });
    }

    // Map a pointer event to the ring whose AREA the point falls in: the
    // smallest circle that contains the point (its band). Hovering anywhere
    // inside a ring activates that ring. Returns -1 when the point is outside
    // every circle (→ intro).
    function ringAt(evt) {
      var rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return -1;
      var x = ((evt.clientX - rect.left) / rect.width) * vb.width;
      var y = ((evt.clientY - rect.top) / rect.height) * vb.height;
      var found = -1;
      var smallestR = Infinity;
      for (var i = 0; i < geom.length; i++) {
        var dx = x - geom[i].cx;
        var dy = y - geom[i].cy;
        if (Math.sqrt(dx * dx + dy * dy) <= geom[i].r && geom[i].r < smallestR) {
          smallestR = geom[i].r;
          found = i;
        }
      }
      return found;
    }

    diagram.addEventListener('mousemove', function (e) {
      if (!desktop.matches) return;
      autoplayPause();
      activate(ringAt(e)); // -1 (outside all circles) reverts to intro
    });

    diagram.addEventListener('mouseleave', function () {
      if (!desktop.matches) return;
      deactivate();
      autoplayPause();
    });

    diagram.addEventListener('click', function (e) {
      autoplayPause();
      activate(ringAt(e));
    });

    for (var l = 0; l < labels.length; l++) {
      (function (idx, label) {
        label.addEventListener('click', function (e) {
          e.stopPropagation();
          autoplayPause();
          activate(idx);
        });
        label.addEventListener('focus', function () {
          autoplayPause();
          activate(idx);
        });
        label.addEventListener('mouseenter', function () {
          if (!desktop.matches) return;
          autoplayPause();
          activate(idx);
        });
      })(l, labels[l]);
    }
  }

  function init(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('[data-ripple]');
    for (var i = 0; i < sections.length; i++) setup(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
